// Which appointment fields flow back to Plato when staff edit them.
//
// Mirrors healo_backend/cmd/plato_sync/outbound/consumer.go
// buildPlatoAppointmentPayload: title, description, starttime/endtime (date +
// start/end), status (code_Left cancelled/booked) and — indirectly — the doctor
// assignment, which Plato receives as the calendar color / code_Background
// location code. Keep in sync with the backend when the outbound payload grows.
import { CloudOff, CloudUpload } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

export const PLATO_SYNCED_APPOINTMENT_FIELDS = Object.freeze([
  "title",
  "description",
  "date",
  "startTime",
  "endTime",
  "doctor",
  "status",
]);

const DEFAULT_TOOLTIP = "Synced to Plato";
const FIELD_TOOLTIPS = {
  doctor: "Synced to Plato via calendar color/location code",
};
const LOCAL_ONLY_TOOLTIP = "Local only — never pushed to Plato";

/**
 * Pill for appointments/entries on a `localOnly` calendar: nothing about them is
 * ever pushed to Plato, so the editor shows this instead of the per-field sync badges.
 */
export function LocalOnlyBadge({ className, label = "Local only" }) {
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={cn(
              "inline-flex items-center gap-1 rounded-md border border-border bg-muted/50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground",
              className,
            )}
            aria-label={LOCAL_ONLY_TOOLTIP}
          >
            <CloudOff className="size-3" aria-hidden="true" />
            {label}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top">{LOCAL_ONLY_TOOLTIP}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * Small inline marker next to a field label indicating the field is written
 * back to Plato on save. Shown on all appointments — every Healo appointment
 * change is pushed outbound when the sync is enabled.
 */
export function PlatoSyncBadge({ field, className }) {
  const label = FIELD_TOOLTIPS[field] || DEFAULT_TOOLTIP;
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={className || "inline-flex items-center text-muted-foreground/70"}
            aria-label={label}
          >
            <CloudUpload className="size-3" aria-hidden="true" />
          </span>
        </TooltipTrigger>
        <TooltipContent side="top">{label}</TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
